import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useRef } from "react";
import { 
  BookOpen, 
  CheckCircle, 
  ArrowRight, 
  Loader2,
  Video,
  Mic,
  TrendingUp,
  Users,
  Target,
  Zap,
  Download,
  AlertTriangle,
  Clock,
  DollarSign,
  Eye,
  Award,
  BarChart3,
  Repeat,
  ListChecks,
  Star,
  Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pain Points Data
const painPoints = [
  {
    icon: Eye,
    title: "Tu competencia es más visible",
    description: "Mientras tú dudas, ellos ya están posicionándose como referentes. Cada día que pasa, pierdes terreno.",
    color: "red"
  },
  {
    icon: Clock,
    title: "Creas contenido sin sistema",
    description: "Posts sueltos que no llevan a ningún lado. Horas en redes sin una estrategia que genere leads.",
    color: "orange"
  },
  {
    icon: DollarSign,
    title: "Marketing sin ROI claro",
    description: "Pagas anuncios, contratas freelancers, pero no sabes qué funciona realmente.",
    color: "purple"
  },
  {
    icon: AlertTriangle,
    title: "Tu marca no transmite autoridad",
    description: "Tienes el conocimiento pero no lo estás comunicando. Tu cliente elige a quien parece más experto.",
    color: "pink"
  }
];

// Benefits Data
const benefits = [
  {
    icon: Target,
    title: "Atrae a tu cliente ideal",
    description: "Quien ve 30 min de tu contenido ya está cualificado y listo para comprar.",
    gradient: "from-blue-500 to-cyan-500"
  },
  {
    icon: Award,
    title: "Posiciónate como el experto",
    description: "Cada episodio construye tu autoridad. En 6 meses serás la primera opción.",
    gradient: "from-purple-500 to-pink-500"
  },
  {
    icon: Repeat,
    title: "1 grabación = 10+ contenidos",
    description: "YouTube, Spotify, LinkedIn, Instagram, TikTok, newsletter... todo desde un rodaje.",
    gradient: "from-pink-500 to-red-500"
  },
  {
    icon: Users,
    title: "Networking de alto nivel",
    description: "Invita a clientes potenciales como invitados. El podcast abre puertas imposibles.",
    gradient: "from-cyan-500 to-blue-500"
  },
  {
    icon: BarChart3,
    title: "Métricas que importan",
    description: "Trackea desde la visualización hasta la venta con datos reales.",
    gradient: "from-blue-500 to-purple-500"
  },
  {
    icon: TrendingUp,
    title: "Activo que crece solo",
    description: "El contenido evergreen sigue generando leads meses después.",
    gradient: "from-purple-500 to-blue-500"
  }
];

// What's Inside Data
const guideContents = [
  {
    icon: Target,
    title: "Estrategia de Contenido",
    items: ["Cómo definir tu audiencia ideal", "Formato perfecto para tu sector", "Calendario de publicación", "Ideas para 50+ episodios"],
    color: "blue"
  },
  {
    icon: Video,
    title: "Producción y Grabación",
    items: ["Setup mínimo viable vs profesional", "Equipamiento recomendado", "Iluminación y sonido", "Técnicas de entrevista"],
    color: "purple"
  },
  {
    icon: Zap,
    title: "Postproducción",
    items: ["Software de edición gratuito", "Flujo de trabajo eficiente", "Creación de clips para RRSS", "Miniaturas que convierten"],
    color: "pink"
  },
  {
    icon: TrendingUp,
    title: "Distribución y Crecimiento",
    items: ["Plataformas donde publicar", "SEO para podcasts", "Promoción orgánica", "Métricas clave a seguir"],
    color: "cyan"
  }
];

// Testimonials
const testimonials = [
  {
    quote: "Esta guía me ahorró meses de prueba y error. Lancé mi podcast en 3 semanas.",
    name: "Carlos R.",
    role: "CEO, Consultora de RRHH"
  },
  {
    quote: "Por fin entendí cómo convertir un podcast en una máquina de leads.",
    name: "Ana M.",
    role: "Directora de Marketing"
  }
];

const colorClasses = {
  red: { bg: "bg-red-50", border: "border-red-200", icon: "bg-red-100 text-red-600" },
  orange: { bg: "bg-orange-50", border: "border-orange-200", icon: "bg-orange-100 text-orange-600" },
  purple: { bg: "bg-purple-50", border: "border-purple-200", icon: "bg-purple-100 text-purple-600" },
  pink: { bg: "bg-pink-50", border: "border-pink-200", icon: "bg-pink-100 text-pink-600" },
  blue: { bg: "bg-blue-50", border: "border-blue-200", icon: "bg-blue-100 text-blue-600" },
  cyan: { bg: "bg-cyan-50", border: "border-cyan-200", icon: "bg-cyan-100 text-cyan-600" },
};

export default function LeadMagnetPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadCount, setDownloadCount] = useState(0);
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    acceptPrivacy: false
  });

  const painRef = useRef(null);
  const benefitsRef = useRef(null);
  const contentsRef = useRef(null);
  const isPainInView = useInView(painRef, { once: true, margin: "-100px" });
  const isBenefitsInView = useInView(benefitsRef, { once: true, margin: "-100px" });
  const isContentsInView = useInView(contentsRef, { once: true, margin: "-100px" });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await axios.get(`${API}/stats/downloads`);
        setDownloadCount(response.data.total_downloads || 0);
      } catch (error) {
        console.log("Stats not available");
      }
    };
    loadStats();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.email) {
      toast.error("Por favor, completa todos los campos");
      return;
    }

    if (!formData.acceptPrivacy) {
      toast.error("Debes aceptar la política de privacidad para continuar");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/lead-magnet`, {
        nombre: formData.nombre,
        email: formData.email,
        accepted_privacy: formData.acceptPrivacy
      });
      navigate("/gracias");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error al enviar. Por favor, inténtalo de nuevo.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById("formulario")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-galvani-bg">
      {/* Header */}
      <header className="sticky top-0 z-50 glass py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 645.4 195.7" className="h-7 w-auto">
                <g fill="#1E293B">
                  <path d="M367.6,80.7c-1-2.1,0.3-4.4,2.9-4.4h17.3c1.6,0,2.6,1,2.9,1.9l16,47.4h0.9L437.3,49c0.7-2,3.4-2.4,4.9-0.9L453.2,60c1.3,1.4,1.6,3.6,0.7,5.4l-43.4,94.1c-0.6,1-1.4,1.9-2.9,1.9H406c-1.6,0-2.4-0.9-2.9-1.9L367.6,80.7z"/>
                  <path d="M320,59.6c0-1.3,1-2.4,2.4-2.4h12.7c1.3,0,2.4,1.1,2.4,2.4l1.1,76l33.4-2.3c1.4,0,2.4,1.1,2.4,2.4v11.9c0,1.3-1,2.4-2.4,2.4h-49.8c-1.4,0-2.4-1.1-2.4-2.4V59.6H320z"/>
                  <path d="M624,59.5c0-1.3,1.1-2.4,2.4-2.4h12.1c1.3,0,2.4,1.1,2.4,2.4v88.1c0,1.3-1.1,2.4-2.4,2.4h-12.1c-1.3,0-2.4-1.1-2.4-2.4V59.5z"/>
                  <path d="M538.8,57.9c-0.7-0.7-1.3-0.9-2.3-0.9h-2c-1.3,0-2.4,1-2.4,2.4v88.2c0,1.3,1.1,2.4,2.4,2.4h12c1.4,0,2.4-1.1,2.4-2.4V91.3h0.3l48,57.8c0.7,0.7,1.3,0.9,2.3,0.9h7.6c1.3,0,2.4-1,2.4-2.4v-14.3L538.8,57.9z"/>
                  <path d="M609.6,126.1V59.5c0-1.3-1.1-2.4-2.4-2.4h-12c-1.4,0-2.4,1.1-2.4,2.4V102L609.6,126.1z"/>
                  <path d="M224,146.6l40.8-88.2c0.4-0.9,1-1.4,2.3-1.4h1.3c1.3,0,1.9,0.7,2.3,1.4l40.4,88.2c0.9,1.7-0.3,3.4-2.3,3.4h-11.4c-2,0-2.9-0.9-3.9-2.6l-7.1-14.1h-37.7l-7.3,14.1c-0.6,1.3-1.7,2.6-3.9,2.6h-11.4C224.2,150,223.2,148.3,224,146.6z M279.6,119.9l-12.1-21.4l-12,21.4H279.6z"/>
                  <path d="M225.7,98.4v2.3c0,26-13.3,49.1-41.8,49.1c-26.8,0-43.8-22.7-43.8-48.1c0-26.3,17.4-46.4,45-46.4c14.4,0,27.1,6.7,34.6,18.7c1.1,1.7,0.4,4-1.3,5l-9.6,5.1c-1.7,0.9-3.7,0.3-4.7-1.3c-4.1-6.6-11.1-10.6-19.4-10.6c-17.3,0-25.8,14.1-25.8,29.4s8.7,32.3,26,32.3c11.3,0,20.7-9.7,21-20.8l-16.7-1c-1.3-0.1-2.1-1.1-2.1-2.4V98.4H225.7z"/>
                  <path d="M437,146.6l40.8-88.2c0.4-0.9,1-1.4,2.3-1.4h1.3c1.3,0,1.9,0.7,2.3,1.4l40.4,88.2c0.9,1.7-0.3,3.4-2.3,3.4h-11.4c-2,0-2.9-0.9-3.9-2.6l-7.1-14.1h-37.7l-7.3,14.1c-0.6,1.3-1.7,2.6-3.9,2.6h-11.4C437.3,150,436.3,148.3,437,146.6z M492.7,119.9l-12.1-21.4l-12,21.4H492.7z"/>
                </g>
              </svg>
            </a>
            <Button
              onClick={scrollToForm}
              className="bg-gradient-to-r from-galvani-blue to-galvani-purple hover:opacity-90 text-white font-body font-semibold px-5 py-2 rounded-full text-sm shadow-lg"
            >
              <Download size={16} className="mr-2" />
              Descargar Gratis
            </Button>
          </div>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        {/* Gradient orbs */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-purple-300/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-300/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-200/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-200 mb-6">
                <Sparkles className="w-4 h-4 text-purple-600" />
                <span className="text-sm text-purple-700 font-body font-medium">Guía Gratuita · 2025</span>
              </div>

              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-galvani-text leading-[1.1] mb-6">
                Crea tu videopodcast
                <span className="gradient-text"> profesional </span>
                y convierte espectadores en clientes
              </h1>

              <p className="font-body text-lg text-galvani-textLight mb-8 leading-relaxed">
                Descarga la guía completa que usan las empresas B2B para lanzar podcasts que 
                <span className="text-galvani-text font-medium"> generan autoridad, leads cualificados y contenido para meses</span>.
              </p>

              {/* Quick benefits */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  <span className="text-sm text-galvani-text">Checklist paso a paso</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  <span className="text-sm text-galvani-text">Plantillas descargables</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  <span className="text-sm text-galvani-text">Herramientas recomendadas</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  <span className="text-sm text-galvani-text">100% Gratis</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={scrollToForm}
                  className="bg-gradient-to-r from-galvani-blue to-galvani-purple hover:opacity-90 text-white font-body font-semibold px-8 py-4 text-base rounded-full transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-500/25"
                >
                  <Download size={18} className="mr-2" />
                  Descargar Guía Gratis
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </div>

              {downloadCount > 0 && (
                <p className="mt-4 text-sm text-galvani-textLight">
                  <span className="font-semibold text-galvani-purple">+{downloadCount}</span> profesionales ya la descargaron
                </p>
              )}
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-400/30 via-purple-400/30 to-pink-400/30 rounded-3xl blur-2xl" />
                <img
                  src="https://images.pexels.com/photos/6953665/pexels-photo-6953665.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Mujer grabando podcast profesional"
                  className="relative rounded-3xl border-4 border-white w-full shadow-2xl"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PAIN POINTS SECTION */}
      <section className="py-20 bg-white" ref={painRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isPainInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs uppercase tracking-[0.2em] text-red-500 font-body font-semibold mb-4">
              ¿Te suena familiar?
            </span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-galvani-text mb-4">
              Los problemas de <span className="text-red-500">no tener</span> un podcast corporativo
            </h2>
            <p className="font-body text-galvani-textLight max-w-2xl mx-auto">
              Si tu empresa no está creando contenido de valor, estás perdiendo oportunidades cada día.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {painPoints.map((point, index) => {
              const colors = colorClasses[point.color];
              return (
                <motion.div
                  key={point.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isPainInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`p-6 rounded-2xl ${colors.bg} border ${colors.border} hover:shadow-lg transition-all`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl ${colors.icon} flex items-center justify-center flex-shrink-0`}>
                      <point.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-lg text-galvani-text mb-2">
                        {point.title}
                      </h3>
                      <p className="font-body text-sm text-galvani-textLight leading-relaxed">
                        {point.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isPainInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-12 text-center"
          >
            <p className="text-galvani-textLight mb-4 font-body">
              La buena noticia: <span className="text-galvani-text font-semibold">tiene solución</span>
            </p>
            <Button
              onClick={scrollToForm}
              variant="outline"
              className="border-galvani-purple text-galvani-purple hover:bg-purple-50 rounded-full"
            >
              Ver cómo solucionarlo
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* BENEFITS SECTION */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50" ref={benefitsRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isBenefitsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs uppercase tracking-[0.2em] text-galvani-blue font-body font-semibold mb-4">
              La Solución
            </span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-galvani-text mb-4">
              Por qué el videopodcast es el <span className="gradient-text">formato ganador</span>
            </h2>
            <p className="font-body text-galvani-textLight max-w-2xl mx-auto">
              Las empresas que dominan su sector ya lo están usando. Aprende cómo hacerlo.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isBenefitsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="p-6 rounded-2xl bg-white border border-slate-200 hover:shadow-xl hover:-translate-y-1 transition-all group"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${benefit.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <benefit.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading font-bold text-lg text-galvani-text mb-2">
                  {benefit.title}
                </h3>
                <p className="font-body text-sm text-galvani-textLight leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isBenefitsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            <div className="text-center p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <span className="font-heading font-extrabold text-3xl text-galvani-blue">82%</span>
              <p className="text-xs text-galvani-textLight mt-1">de decisores B2B consume podcasts</p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <span className="font-heading font-extrabold text-3xl text-galvani-purple">4.5x</span>
              <p className="text-xs text-galvani-textLight mt-1">más engagement que blogs</p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <span className="font-heading font-extrabold text-3xl text-galvani-pink">67%</span>
              <p className="text-xs text-galvani-textLight mt-1">recuerda la marca después</p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <span className="font-heading font-extrabold text-3xl text-galvani-cyan">10+</span>
              <p className="text-xs text-galvani-textLight mt-1">piezas de contenido por episodio</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHAT'S INSIDE SECTION */}
      <section className="py-20 bg-white" ref={contentsRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isContentsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs uppercase tracking-[0.2em] text-galvani-purple font-body font-semibold mb-4">
              Contenido de la Guía
            </span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-galvani-text mb-4">
              Todo lo que incluye la <span className="gradient-text">guía gratuita</span>
            </h2>
            <p className="font-body text-galvani-textLight max-w-2xl mx-auto">
              +40 páginas de contenido práctico y accionable. Sin relleno, solo lo que necesitas.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {guideContents.map((section, index) => {
              const colors = colorClasses[section.color];
              return (
                <motion.div
                  key={section.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isContentsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-6 lg:p-8 rounded-2xl bg-slate-50 border border-slate-200 hover:shadow-lg transition-all"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-12 h-12 rounded-xl ${colors.icon} flex items-center justify-center`}>
                      <section.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-galvani-text">
                      {section.title}
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="font-body text-sm text-galvani-textLight">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>

          {/* Bonus */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isContentsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-12 p-6 lg:p-8 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 text-white"
          >
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <ListChecks className="w-8 h-8 text-white" />
              </div>
              <div className="text-center md:text-left flex-1">
                <h3 className="font-heading font-bold text-xl mb-2">
                  BONUS: Checklist de Lanzamiento
                </h3>
                <p className="font-body text-white/90 text-sm">
                  Incluye un checklist imprimible con los 47 pasos exactos para lanzar tu podcast sin olvidar nada.
                </p>
              </div>
              <Button
                onClick={scrollToForm}
                className="bg-white text-purple-600 hover:bg-white/90 font-body font-semibold px-6 py-3 rounded-full"
              >
                Quiero el Checklist
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((t, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="font-body text-galvani-text text-sm mb-4 italic">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div>
                  <p className="font-heading font-semibold text-galvani-text text-sm">{t.name}</p>
                  <p className="font-body text-xs text-galvani-textLight">{t.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM SECTION */}
      <section id="formulario" className="py-20 bg-galvani-dark">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-8">
              <span className="inline-block text-xs uppercase tracking-[0.2em] text-galvani-cyan font-body font-medium mb-4">
                Descarga Gratuita
              </span>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-white mb-4">
                Consigue tu guía ahora
              </h2>
              <p className="font-body text-slate-400">
                Déjanos tu email y te la enviamos al instante. Sin spam, lo prometemos.
              </p>
            </div>

            <div className="p-8 rounded-2xl bg-slate-800/50 border border-slate-700 shadow-2xl">
              {downloadCount > 0 && (
                <div className="flex items-center justify-center gap-2 mb-6 py-2 px-4 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30">
                  <Download className="w-4 h-4 text-blue-400" />
                  <span className="text-sm text-blue-300 font-body font-medium">
                    +{downloadCount} descargas
                  </span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label className="text-slate-300 text-sm mb-2 block font-body">
                    Nombre *
                  </Label>
                  <Input
                    type="text"
                    placeholder="Tu nombre"
                    value={formData.nombre}
                    onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                    className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 h-12 rounded-xl"
                    data-testid="leadmagnet-nombre-input"
                    required
                  />
                </div>

                <div>
                  <Label className="text-slate-300 text-sm mb-2 block font-body">
                    Email *
                  </Label>
                  <Input
                    type="email"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 h-12 rounded-xl"
                    data-testid="leadmagnet-email-input"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-galvani-blue to-galvani-purple hover:opacity-90 text-white font-body font-semibold py-4 h-14 text-lg rounded-full transition-all duration-300 hover:scale-[1.02] shadow-lg shadow-blue-500/25"
                  data-testid="leadmagnet-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5 mr-2" />
                      Descargar Guía Gratis
                    </>
                  )}
                </Button>

                {/* Privacy checkbox */}
                <div className="flex items-start gap-3 pt-2">
                  <Checkbox
                    id="accept-privacy"
                    checked={formData.acceptPrivacy}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, acceptPrivacy: checked === true }))
                    }
                    className="mt-0.5 border-slate-500 data-[state=checked]:bg-galvani-purple data-[state=checked]:border-galvani-purple"
                    data-testid="leadmagnet-privacy-checkbox"
                  />
                  <label
                    htmlFor="accept-privacy"
                    className="text-xs text-slate-400 font-body leading-relaxed cursor-pointer select-none"
                  >
                    He leído y acepto la{" "}
                    <a
                      href="/politica-privacidad"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-galvani-cyan hover:underline font-medium"
                    >
                      política de privacidad
                    </a>
                    {" "}y consiento el tratamiento de mis datos para recibir la guía y comunicaciones de Galvani Studios.
                  </label>
                </div>
              </form>

              <div className="mt-6 pt-6 border-t border-slate-700">
                <div className="flex items-center justify-center gap-6 text-slate-500 text-xs font-body">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>100% Gratis</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Sin spam</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Entrega inmediata</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-galvani-textLight font-body text-sm text-center md:text-left">
              © {new Date().getFullYear()} Galvani Studios SL · N.I.F. B42810549
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm">
              <a href="/politica-privacidad" className="text-galvani-textLight hover:text-galvani-purple font-body transition-colors" data-testid="leadmagnet-footer-privacy-link">
                Política de Privacidad
              </a>
              <a href="/politica-cookies" className="text-galvani-textLight hover:text-galvani-purple font-body transition-colors" data-testid="leadmagnet-footer-cookies-link">
                Política de Cookies
              </a>
              <a href="/" className="text-galvani-blue hover:text-galvani-purple font-body transition-colors">
                ← Volver al inicio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
